"use client";

import { FC } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import PlayIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Autorenew";

import { useStyles } from "./styles";
import { QuizResult, QuizType } from "@/types/quiz";
import { useAppSelector } from "@/redux/store";
import QuizRating from "@/components/quiz/common/QuizRating";
import ShareControls from "@/components/quiz/common/ShareControls";
import QuizComments from "@/components/quiz/common/QuizComments";
import Button from "@/components/common/PrimaryButton";
import Watermark from "@/components/common/Watermark";

interface QuizResultControlsProps {
  results: QuizResult;
}

const QuizResultControls: FC<QuizResultControlsProps> = ({ results }) => {
  const { classes, cx } = useStyles();
  const isLargeScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.up("lg")
  );
  const router = useRouter();
  const user = useAppSelector((state) => state.user.user);
  const isSameUser = Boolean(user && user.id === results.userId);
  const takeQuizButtonText = isSameUser ? "Try Again" : "Take Quiz";
  const isBusiness = results.isBusiness;

  const handleRetry = () => {
    router.push(`/quiz/${results.quiz.slug}`);
  };

  return (
    <>
      {!isBusiness && (
        <QuizRating
          slug={results.quiz.slug}
          userRating={results.quiz.userRating}
          size={isLargeScreen ? "46px" : "33px"}
        />
      )}
      <div className={classes.divider} />
      <div className={classes.controlGroupRow}>
        <div className={classes.controlGroup}>
          <p className={classes.controlGroupLabel}>Share This Result</p>
          <ShareControls
            title={`${results.quiz.title} - ${
              results.quiz.quizType === QuizType.QuestionAnswer
                ? "can you beat my score?"
                : "what's your result?"
            }`}
            description={results.quiz.description}
            thumbnailUrl={
              results.relatedImageUrl ||
              results.quiz.relatedImageUrl ||
              "https://files.idyllic.app/files/static/idyllic-logo.png"
            }
          />
        </div>
        <div className={classes.controlGroup}>
          <p className={classes.controlGroupLabel}>Comments</p>
          <QuizComments
            quizSlug={results.quiz.slug}
            commentCount={results.quiz.commentCount}
          />
        </div>
        {isLargeScreen && (
          <Button
            variant="outlined"
            onClick={handleRetry}
            color="secondary"
            icon={isSameUser ? <RefreshIcon /> : <PlayIcon />}
            className={classes.button}
          >
            {takeQuizButtonText}
          </Button>
        )}
      </div>
      {!isLargeScreen && !isBusiness && (
        <div className={classes.controlGroupRow}>
          <div className={classes.controlGroup}>
            <Button
              variant="outlined"
              onClick={handleRetry}
              color="secondary"
              icon={isSameUser ? <RefreshIcon /> : <PlayIcon />}
              className={classes.button}
            >
              {takeQuizButtonText}
            </Button>
          </div>
          <div className={classes.controlGroup}>
            <div className={classes.controlGroup}>
              <Link href="/quiz" id="all-quizzes">
                <Button color="secondary" className={classes.button}>
                  All Quizzes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
      {!isBusiness && (
        <div className={cx(classes.controlGroupRow, classes.bottomGroupMobile)}>
          <div className={classes.controlGroup}>
            <Watermark />
          </div>
          <div className={classes.controlGroup}>
            <p className={classes.promotionalText}>
              New Quizzes & Global Leaderboard Daily
            </p>
          </div>
          {isLargeScreen && (
            <div className={classes.controlGroup}>
              <Link href="/quiz" id="all-quizzes">
                <Button color="secondary" className={classes.button}>
                  All Quizzes
                </Button>
              </Link>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default QuizResultControls;
